/* eslint-disable max-lines */
/**
 * SlideBuilder
 *
 * Right panel of the Slide Sense page where users assemble their presentation.
 *
 * Acts as a drop target via `useDroppable` from @dnd-kit/core.
 * Contains a `SortableContext` for reordering slides within the builder.
 * Shows an empty state when no slides have been added, and a footer with the
 * MergeDownloadButton for exporting the assembled presentation.
 */

import { useEffect, useMemo, useRef, useState } from "react"

import { useDroppable } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SlideSensePresenterModes } from "../api"
import type { BuilderSlide, RemovedSlide } from "../types"

import {
  AlertTriangle,
  ChevronDown,
  FilePlus,
  Layers,
  Loader2,
  MonitorPlay,
  Play,
  Plus,
  Save,
  X,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

import { BuilderSlideItem } from "./BuilderSlideItem"
import { MergeDownloadButton } from "./MergeDownloadButton"
import { createCollection, updateCollection } from "../api"
import {
  hasSeenPreviewModeHint,
  markPreviewModeHintSeen,
  previewHintScopeId,
  rotateDraftPreviewHintScopeId,
  transferPreviewHintSeen,
} from "../utils/preview-mode-hint"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SlideBuilderProps {
  slides: BuilderSlide[]
  onRemove: (builderId: string) => void
  onClear: () => void
  /** True when a library slide (not a builder reorder) is being dragged. */
  isLibraryDragging?: boolean
  /** ID of the builder item currently hovered during a library drag. */
  overItemId?: string | null
  /** When the builder was loaded from a saved collection, its ID. */
  activeCollectionId?: string | null
  /** Name of the active collection. */
  activeCollectionName?: string | null
  /** Slide IDs as they were the last time the active collection was loaded or saved. */
  savedSlideIds?: string[]
  /** Called after successfully saving/updating so the page can track the new active collection. */
  onCollectionSaved?: (id: string, name: string) => void
  /** Slides an admin removed from this collection after it was last saved. */
  removedSlides?: RemovedSlide[]
  /** Dismiss the removed-slides banner (page clears state + POSTs acknowledge-removals). */
  onAcknowledgeRemovals?: () => void
  /**
   * Called when the user clicks "Preview" — the handler is expected
   * to request fullscreen synchronously (preserving the user gesture) and
   * flip the parent's presenting state.  Disabled when `slides.length === 0`.
   */
  onStartPresenting?: () => void
  /**
   * Called when the user clicks "Preview with speaker notes". The
   * parent is expected to mint a session id, open the presenter window
   * via `window.open()`, request fullscreen on the main window, and
   * mount the PresentationMode with the matching session id so the
   * two windows stay in sync.
   */
  onStartPresentingWithPresenterView?: () => void
  /**
   * Called by MergeDownloadButton when the merge endpoint returns
   * unmergeable slides — the parent should drop the listed slide IDs
   * from the builder so the user can retry.
   */
  onRemoveSlidesFromBuilder?: (slideIds: string[]) => void
  /**
   * Which presenter modes the tenant admin has enabled. Disabled modes are
   * hidden from the Present control entirely (not just greyed out). Defaults
   * to both enabled so a missing prop preserves the full menu.
   */
  presenterModes?: SlideSensePresenterModes
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SlideBuilder({
  slides,
  onRemove,
  onClear,
  isLibraryDragging = false,
  overItemId = null,
  activeCollectionId = null,
  activeCollectionName = null,
  savedSlideIds,
  onCollectionSaved,
  removedSlides = [],
  onAcknowledgeRemovals,
  onStartPresenting,
  onStartPresentingWithPresenterView,
  onRemoveSlidesFromBuilder,
  presenterModes = { presentation: true, presenter_view: true },
}: SlideBuilderProps) {
  const { t, i18n } = useTranslation()
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: "slide-builder-drop-zone",
  })
  const queryClient = useQueryClient()

  // Compose dnd-kit's droppable ref with our own ref so we can read the
  // wrapper's bounding rect to decide whether the pointer is closer to the
  // top or bottom edge while the cursor is over empty/padding regions of an
  // overflowing list. dnd-kit's `over.id` only tells us *which* droppable
  // we're over, not where on it. (#63)
  const wrapperElRef = useRef<HTMLDivElement | null>(null)
  const setNodeRef = (node: HTMLDivElement | null) => {
    wrapperElRef.current = node
    setDroppableRef(node)
  }

  // "top" | "bottom" | null — which edge the cursor sits over while dragging
  // a library card and the dnd `over` is the wrapper itself (not a child).
  // Drives the leading/trailing placeholder visibility.
  const [edgeHint, setEdgeHint] = useState<"top" | "bottom" | null>(null)

  // Track pointer Y vs wrapper midpoint while a library drag is active so we
  // can tell apart "above first slide" from "below last slide" when the dnd
  // over-target is the wrapper itself (overflowing list, padding regions).
  useEffect(() => {
    if (!isLibraryDragging) {
      setEdgeHint(null)
      return
    }
    const handler = (e: PointerEvent) => {
      const el = wrapperElRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        setEdgeHint(null)
        return
      }
      const mid = rect.top + rect.height / 2
      setEdgeHint(e.clientY < mid ? "top" : "bottom")
    }
    window.addEventListener("pointermove", handler)
    return () => window.removeEventListener("pointermove", handler)
  }, [isLibraryDragging])

  // Stable list of ids for SortableContext
  const slideIds = useMemo(() => slides.map((s) => s.id), [slides])

  // #82 — show "unsaved changes" when the loaded collection's slide list
  // differs from what's currently in the builder (different slides or order).
  const isDirty = useMemo(() => {
    if (!activeCollectionId || !savedSlideIds) return false
    const current = slides.map((bs) => bs.slide.id)
    if (current.length !== savedSlideIds.length) return true
    for (let i = 0; i < current.length; i++) {
      if (current[i] !== savedSlideIds[i]) return true
    }
    return false
  }, [activeCollectionId, savedSlideIds, slides])

  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveName, setSaveName] = useState("")
  const [isSaving, setIsSaving] = useState(false)

  const defaultName = () => {
    const d = new Date()
    const dateStr = d.toLocaleDateString(i18n.language, {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    return t("slideSense.defaultPresentationName", { date: dateStr })
  }

  const handleOpenSaveDialog = () => {
    setSaveName(activeCollectionName || defaultName())
    setSaveDialogOpen(true)
  }

  const handleSave = async (mode: "new" | "update" = "new") => {
    if (!saveName.trim() || slides.length === 0) return
    setIsSaving(true)
    try {
      const slideIdsList = slides.map((bs) => bs.slide.id)

      if (mode === "update" && activeCollectionId) {
        await updateCollection(activeCollectionId, {
          name: saveName.trim(),
          slide_ids: slideIdsList,
        })
        toast.success(t("slideSense.collectionUpdated", { name: saveName.trim() }))
        onCollectionSaved?.(activeCollectionId, saveName.trim())
      } else {
        const created = await createCollection({ name: saveName.trim(), slide_ids: slideIdsList })
        toast.success(t("slideSense.collectionSaved", { name: saveName.trim() }))
        if (!activeCollectionId) {
          transferPreviewHintSeen(previewHintScopeId(null), previewHintScopeId(created.id))
        }
        onCollectionSaved?.(created.id, saveName.trim())
      }

      setSaveDialogOpen(false)
      void queryClient.invalidateQueries({ queryKey: ["slide-sense", "collections"] })
    } catch (err) {
      console.error("Operation failed:", err)
      toast.error(t("slideSense.failedToSaveCollection"))
    } finally {
      setIsSaving(false)
    }
  }

  // Preview-mode dialog: once per presentation in this tab session.
  const [previewHintOpen, setPreviewHintOpen] = useState(false)
  const [pendingPreview, setPendingPreview] = useState<"audience" | "presenter" | null>(null)

  const startPreview = (mode: "audience" | "presenter") => {
    if (mode === "presenter") {
      onStartPresentingWithPresenterView?.()
      return
    }
    onStartPresenting?.()
  }

  const requestPreview = (mode: "audience" | "presenter") => {
    if (hasSeenPreviewModeHint(previewHintScopeId(activeCollectionId))) {
      startPreview(mode)
      return
    }
    setPendingPreview(mode)
    setPreviewHintOpen(true)
  }

  const continuePreview = () => {
    markPreviewModeHintSeen(previewHintScopeId(activeCollectionId))
    setPreviewHintOpen(false)
    if (pendingPreview) startPreview(pendingPreview)
    setPendingPreview(null)
  }

  const handleStartNew = () => {
    rotateDraftPreviewHintScopeId()
    onClear()
  }

  // Present control shape depends on which presenter modes the tenant admin
  // has enabled (#2336 H). Disabled modes are hidden, not greyed. Built here
  // (not inline) to keep the JSX free of nested ternaries.
  const renderPresentControl = () => {
    const previewBtnClass = "h-8 gap-1.5 px-2.5 text-xs"
    const audienceButton = (className: string) => (
      <Button
        className={className}
        data-testid="slide-builder-presentera"
        disabled={slides.length === 0 || !onStartPresenting}
        size="sm"
        type="button"
        variant="outline"
        aria-label={
          slides.length === 0
            ? t("slideSense.present.disabledEmpty")
            : t("slideSense.present.playButton")
        }
        title={
          slides.length === 0
            ? t("slideSense.present.disabledEmpty")
            : t("slideSense.present.playButton")
        }
        onClick={() => requestPreview("audience")}
      >
        <Play className="h-3.5 w-3.5" />
        <span>{t("slideSense.present.playButton")}</span>
      </Button>
    )

    const presenterButton = (className: string) => (
      <Button
        aria-label={t("slideSense.present.playWithPresenter")}
        className={className}
        data-testid="slide-builder-presentera-with-presenter"
        disabled={slides.length === 0 || !onStartPresentingWithPresenterView}
        size="sm"
        title={t("slideSense.present.playWithPresenter")}
        type="button"
        variant="outline"
        onClick={() => requestPreview("presenter")}
      >
        <MonitorPlay className="h-3.5 w-3.5" />
        <span>{t("slideSense.present.playButton")}</span>
      </Button>
    )

    if (presenterModes.presentation && presenterModes.presenter_view) {
      return (
        <div className="flex items-center">
          {audienceButton(`${previewBtnClass} rounded-r-none`)}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t("slideSense.present.moreOptions")}
                className="h-8 rounded-l-none border-l-0 px-1.5 text-xs"
                data-testid="slide-builder-presentera-menu"
                disabled={slides.length === 0}
                size="sm"
                type="button"
                variant="outline"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                data-testid="slide-builder-presentera-audience"
                disabled={slides.length === 0 || !onStartPresenting}
                onSelect={() => requestPreview("audience")}
              >
                <Play className="mr-2 h-4 w-4" />
                {t("slideSense.present.playButton")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="slide-builder-presentera-with-presenter"
                disabled={slides.length === 0 || !onStartPresentingWithPresenterView}
                onSelect={() => requestPreview("presenter")}
              >
                <MonitorPlay className="mr-2 h-4 w-4" />
                {t("slideSense.present.playWithPresenter")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
    if (presenterModes.presentation) return audienceButton(previewBtnClass)
    if (presenterModes.presenter_view) return presenterButton(previewBtnClass)
    return null
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header — wraps to a second row on narrow widths so the action group never clips. */}
      <div className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-2 border-b px-5 py-2">
        <div className="flex min-w-0 flex-1 basis-[180px] items-center gap-2">
          <Layers className="text-muted-foreground h-4 w-4 shrink-0" />
          <h2 className="truncate text-sm font-semibold" title={activeCollectionName || undefined}>
            {activeCollectionName || t("slideSense.builder")}
          </h2>
          {slides.length > 0 && (
            <span className="text-muted-foreground shrink-0 text-xs font-normal tabular-nums">
              {t("slideSense.slideCount", { count: slides.length })}
            </span>
          )}
          {isDirty && (
            <span
              aria-live="polite"
              className="text-warning-ink inline-flex shrink-0 items-center gap-1 text-[11px] font-medium"
              title={t("slideSense.unsavedChangesHint")}
            >
              <span aria-hidden="true" className="bg-warning h-1.5 w-1.5 rounded-full" />
              {t("slideSense.unsavedChanges")}
            </span>
          )}
        </div>
        {slides.length > 0 && (
          <div className="flex shrink-0 items-center gap-1">
            {renderPresentControl()}
            <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  aria-label={t("slideSense.save")}
                  className="text-muted-foreground hover:text-primary h-9 w-9 p-0"
                  size="sm"
                  title={t("slideSense.save")}
                  variant="ghost"
                  onClick={handleOpenSaveDialog}
                >
                  <Save className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t("slideSense.saveCollection")}</DialogTitle>
                  <DialogDescription>
                    {t("slideSense.saveDescription", { count: slides.length })}
                  </DialogDescription>
                </DialogHeader>
                <Input
                  aria-label={t("slideSense.collectionName")}
                  maxLength={100}
                  placeholder={t("slideSense.collectionName")}
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      void handleSave(activeCollectionId ? "update" : "new")
                    }
                  }}
                />
                <DialogFooter>
                  <Button
                    disabled={isSaving}
                    size="sm"
                    variant="outline"
                    onClick={() => setSaveDialogOpen(false)}
                  >
                    {t("slideSense.cancel")}
                  </Button>
                  {activeCollectionId ? (
                    <>
                      <Button
                        disabled={!saveName.trim() || isSaving}
                        size="sm"
                        variant="outline"
                        onClick={() => handleSave("new")}
                      >
                        {t("slideSense.saveAsNew")}
                      </Button>
                      <Button
                        disabled={!saveName.trim() || isSaving}
                        size="sm"
                        onClick={() => handleSave("update")}
                      >
                        {isSaving ? (
                          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="mr-1 h-4 w-4" />
                        )}
                        {t("slideSense.update")}
                      </Button>
                    </>
                  ) : (
                    <Button
                      disabled={!saveName.trim() || isSaving}
                      size="sm"
                      onClick={() => handleSave("new")}
                    >
                      {isSaving ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-1 h-4 w-4" />
                      )}
                      {t("slideSense.save")}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <MergeDownloadButton
              presentationName={activeCollectionName}
              slides={slides}
              onRemoveSlides={onRemoveSlidesFromBuilder}
            />
            <div aria-hidden="true" className="bg-border mx-1 h-5 w-px" />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  aria-label={t("slideSense.startNew")}
                  className="text-muted-foreground hover:text-primary h-9 w-9 p-0"
                  size="sm"
                  title={t("slideSense.startNew")}
                  variant="ghost"
                >
                  <FilePlus className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("slideSense.startNewConfirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("slideSense.startNewConfirmBody", { count: slides.length })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("slideSense.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleStartNew}>
                    {t("slideSense.startNewConfirmAction")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {/* Removed-slides notification — shown when an admin deleted slides
          from a saved collection since the user last opened it. */}
      {removedSlides.length > 0 && (
        <div className="border-warning-ink bg-warning/30 text-warning-ink shrink-0 border-b px-4 py-2.5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-warning-ink mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1 text-xs">
              <p className="font-medium">
                {t("slideSense.adminRemovedSlides", { count: removedSlides.length })}
              </p>
              {(() => {
                const sources = Array.from(
                  new Set(removedSlides.map((r) => r.deck_name || r.folder_name).filter(Boolean))
                )
                return sources.length > 0 ? (
                  <p className="text-warning-ink/80 mt-0.5">
                    {t("slideSense.adminRemovedSlidesDetail", {
                      sources: sources.join(", "),
                    })}
                  </p>
                ) : null
              })()}
            </div>
            {onAcknowledgeRemovals && (
              <Button
                className="text-warning-ink hover:bg-warning/30 h-6 shrink-0 gap-1 px-1.5 text-xs"
                size="sm"
                variant="ghost"
                onClick={onAcknowledgeRemovals}
              >
                <X className="h-3 w-3" />
                {t("slideSense.dismissNotice")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Drop zone + sortable list */}
      <div
        ref={setNodeRef}
        className={cn(
          "min-h-0 flex-1 overflow-hidden transition-colors",
          isOver && "bg-primary/5",
          isOver && isLibraryDragging && "ring-primary/30 ring-2 ring-inset"
        )}
      >
        {/* dnd-kit's closestCenter picks BuilderSlideItem as `over` once the cursor
            enters one — wrapper's isOver flips false in that case. So gate the per-slide
            placeholder on `isLibraryDragging` + matching `overItemId`, not on isOver. */}
        {slides.length === 0 ? (
          <div className="relative flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <Layers className="text-muted-foreground/40 h-10 w-10" />
            <p className="text-muted-foreground text-sm">{t("slideSense.dragToBuilder")}</p>
            {/* Empty-builder placeholder — fades in while a library card is over the zone */}
            {isLibraryDragging && isOver && (
              <div className="border-primary/40 bg-primary/5 text-primary/60 animate-in fade-in absolute inset-4 flex items-center justify-center rounded-lg border-2 border-dashed duration-150">
                <Plus className="h-6 w-6" />
              </div>
            )}
          </div>
        ) : (
          // Plain overflow-y-auto instead of Radix ScrollArea: Radix wraps
          // the content in a div with ``min-width: 100%`` that's measured
          // against its own content box, which causes the cards to widen
          // past the panel and clip at the viewport when the user drags
          // the panel narrow. (#10)
          <div className="h-full overflow-x-hidden overflow-y-auto">
            <div className="space-y-5 p-3">
              {/* Leading placeholder — shown when the cursor is over the wrapper
                  (not a specific slide) and closer to the top edge. Without
                  this, dragging onto an overflowing list while the first slide
                  is scrolled out of view shows no indicator. (#63) */}
              {(() => {
                const overWrapper =
                  isLibraryDragging && (overItemId == null || !slideIds.includes(overItemId))
                const showLeading = overWrapper && edgeHint === "top"
                return (
                  <div
                    aria-hidden={!showLeading}
                    className={cn(
                      "grid transition-[grid-template-rows,margin] duration-200 ease-out",
                      showLeading ? "mb-2 grid-rows-[1fr]" : "mb-0 grid-rows-[0fr]"
                    )}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div className="border-primary/40 bg-primary/5 text-primary/60 flex h-20 items-center justify-center rounded-lg border-2 border-dashed">
                        <Plus className="h-5 w-5" />
                      </div>
                    </div>
                  </div>
                )
              })()}
              <SortableContext items={slideIds} strategy={verticalListSortingStrategy}>
                {slides.map((bs, idx) => {
                  const showPlaceholderHere = isLibraryDragging && overItemId === bs.id
                  return (
                    <div key={bs.id}>
                      {/* grid-template-rows trick: 0fr -> 1fr animates smoothly without
                          knowing intrinsic content height. */}
                      <div
                        aria-hidden={!showPlaceholderHere}
                        className={cn(
                          "grid transition-[grid-template-rows,margin] duration-200 ease-out",
                          showPlaceholderHere ? "mb-2 grid-rows-[1fr]" : "mb-0 grid-rows-[0fr]"
                        )}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <div className="border-primary/40 bg-primary/5 text-primary/60 flex h-20 items-center justify-center rounded-lg border-2 border-dashed">
                            <Plus className="h-5 w-5" />
                          </div>
                        </div>
                      </div>
                      <BuilderSlideItem builderSlide={bs} index={idx + 1} onRemove={onRemove} />
                    </div>
                  )
                })}
              </SortableContext>
              {/* Trailing drop zone — explicit ``useDroppable`` that catches
                  cursors below the last slide. Without this, dnd-kit's
                  closestCenter keeps reporting the last slide as the
                  over-target even when the cursor is far below it (its
                  center is closer than the wrapper's geometric center
                  when there is empty space below), so the "insert above"
                  placeholder fires and the new slide lands second-to-last
                  instead of last. (#63) */}
              <TrailingDropZone
                edgeHint={edgeHint}
                isLibraryDragging={isLibraryDragging}
                overItemId={overItemId}
                slideIds={slideIds}
              />
            </div>
          </div>
        )}
      </div>

      <Dialog
        open={previewHintOpen}
        onOpenChange={(open) => {
          setPreviewHintOpen(open)
          if (!open) setPendingPreview(null)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("slideSense.present.previewModeTitle")}</DialogTitle>
            <DialogDescription>{t("slideSense.present.previewModeBody")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button size="sm" type="button" variant="outline" onClick={continuePreview}>
              {t("slideSense.present.previewModeContinue")}
            </Button>
            <MergeDownloadButton
              presentationName={activeCollectionName}
              slides={slides}
              testId="slide-builder-preview-mode-download"
              onRemoveSlides={onRemoveSlidesFromBuilder}
              onDownloaded={() => {
                markPreviewModeHintSeen(previewHintScopeId(activeCollectionId))
                setPreviewHintOpen(false)
                setPendingPreview(null)
              }}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Trailing drop zone — sits below the last builder slide and accepts library
// drags that land in the empty space at the bottom of the panel. Its own
// useDroppable id means dnd-kit's closestCenter resolves cleanly to "trail"
// when the cursor is closer to it than to any sortable slide.
// ---------------------------------------------------------------------------

interface TrailingDropZoneProps {
  isLibraryDragging: boolean
  overItemId: string | null
  edgeHint: "top" | "bottom" | null
  slideIds: string[]
}

function TrailingDropZone({
  isLibraryDragging,
  overItemId,
  edgeHint,
  slideIds,
}: TrailingDropZoneProps) {
  const { setNodeRef, isOver } = useDroppable({ id: "slide-builder-trail" })
  // Show the trailing placeholder when (a) dnd-kit reports the trail itself
  // as over, or (b) the wrapper is the over-target and the cursor is past
  // its midpoint (legacy edgeHint path so the indicator still appears the
  // moment the drag enters near the bottom even before the cursor reaches
  // the trail's own bounding rect).
  const overWrapper = isLibraryDragging && (overItemId == null || !slideIds.includes(overItemId))
  const showTrailing = isLibraryDragging && (isOver || (overWrapper && edgeHint !== "top"))

  return (
    <div
      ref={setNodeRef}
      aria-hidden={!showTrailing}
      className={cn(
        "grid transition-[grid-template-rows,margin] duration-200 ease-out",
        showTrailing ? "mt-2 grid-rows-[1fr]" : "mt-0 grid-rows-[0fr]",
        // Keep a small reserved hit area while dragging so the cursor has
        // somewhere unambiguous to land. Collapses to 0 once the drag ends.
        isLibraryDragging && "min-h-16"
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="border-primary/40 bg-primary/5 text-primary/60 flex h-20 items-center justify-center rounded-lg border-2 border-dashed">
          <Plus className="h-5 w-5" />
        </div>
      </div>
    </div>
  )
}
